import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Modal } from "react-bootstrap";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useTranslation } from "react-i18next";
import "./CanvasTutorialBadge.css";

const CanvasTutorialBadge = ({ visible = false }) => {
  const { t, i18n } = useTranslation(["translation"]);
  const [portalTarget, setPortalTarget] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("canvasTutorialBadgeCollapsed") === "true";
    } catch (error) {
      return false;
    }
  });
  const [show, setShow] = useState(false);
  const videoRef = useRef(null);

  const locale = i18n.language?.toLowerCase() || "en";
  const isFrenchLocale = locale.startsWith("fr");

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) {
      return undefined;
    }

    if (show) {
      videoElement.currentTime = 0;
      const playPromise = videoElement.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    } else {
      videoElement.pause();
      videoElement.currentTime = 0;
    }

    return undefined;
  }, [show]);

  useEffect(() => {
    if (!visible) {
      setPortalTarget(null);
      return undefined;
    }

    let isMounted = true;

    const resolvePortalTarget = () => {
      if (!isMounted) {
        return;
      }

      const target =
        document.querySelector(".canvasMainArea") ||
        document.querySelector(".canvasContainer") ||
        document.body;

      setPortalTarget(target);
    };

    resolvePortalTarget();
    const timer = window.setTimeout(resolvePortalTarget, 250);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [visible]);

  if (!visible || !portalTarget) {
    return null;
  }

  const positionClass = portalTarget === document.body ? " canvas-tutorial-badge-fixed" : "";

  const handleCollapseToggle = (nextValue) => {
    setCollapsed(nextValue);
    try {
      localStorage.setItem("canvasTutorialBadgeCollapsed", nextValue ? "true" : "false");
    } catch (error) {}
  };

  const badge = collapsed ? (
    <button
      type="button"
      className={`canvas-tutorial-badge canvas-tutorial-badge-collapsed${positionClass}`}
      onClick={() => handleCollapseToggle(false)}
      aria-label={t("tutorialVideo.expand")}
      title={t("tutorialVideo.expand")}
      data-onboarding="video-tutorial"
    >
      <PlayCircleOutlineIcon className="canvas-tutorial-badge-icon" />
    </button>
  ) : (
    <div className={`canvas-tutorial-badge canvas-tutorial-badge-expanded${positionClass}`}>
      <button
        type="button"
        className="canvas-tutorial-badge-main"
        onClick={() => setShow(true)}
        aria-label={t("tutorialVideo.badge")}
        data-onboarding="video-tutorial"
      >
        <PlayCircleOutlineIcon className="canvas-tutorial-badge-icon" />
        <span>{t("tutorialVideo.badge")}</span>
      </button>
      <button
        type="button"
        className="canvas-tutorial-badge-collapse"
        onClick={() => handleCollapseToggle(true)}
        aria-label={t("tutorialVideo.collapse")}
        title={t("tutorialVideo.collapse")}
      >
        ×
      </button>
    </div>
  );

  return (
    <>
      {!show ? createPortal(badge, portalTarget) : null}

      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="lg"
        centered
        aria-labelledby="canvas-tutorial-video-title"
        className="canvas-tutorial-modal-root"
        backdropClassName="canvas-tutorial-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title id="canvas-tutorial-video-title">
            {t("tutorialVideo.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="video-container">
            <video
              ref={videoRef}
              controls
              playsInline
              className="canvas-tutorial-video"
            >
              <source src="/site-images-videos/new-design-en.mp4" type="video/mp4" />
              <track
                kind="captions"
                src="/site-images-videos/new-design-fr.vtt"
                srcLang="fr"
                label="Francais"
                default={isFrenchLocale}
              />
              <track
                kind="captions"
                src="/site-images-videos/new-design-en.vtt"
                srcLang="en"
                label={isFrenchLocale ? "Anglais" : "English"}
                default={!isFrenchLocale}
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            {t("modal-close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CanvasTutorialBadge;
